// canvas.js — Upward-drifting char particle system

const CHARS = '01/{|}[]<>:;!@#*∿⌬◌·※⊕∘○+×÷≈∞_—';

const PALETTE_DARK  = ['#00bf72', '#00e68a', '#4ecdc4', '#c8f5e1', '#6b9e82', '#3d9e6a'];
const PALETTE_LIGHT = ['#1a5c38', '#0f3d24', '#2d6a5a', '#4a7060', '#1f7a50', '#327050'];

let canvas, ctx;
let particles = [];
let animId = null;
let frame = 0;
let w = 0, h = 0;
let isDark = true;
let paused = false;
const PARTICLE_COUNT = 70;

function palette() {
  return isDark ? PALETTE_DARK : PALETTE_LIGHT;
}

function randomFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeParticle(spawnAtBottom = true) {
  return {
    x: Math.random() * w,
    y: spawnAtBottom ? h + Math.random() * 200 : Math.random() * h,
    vy: 0.25 + Math.random() * 0.65,
    kickX: 0,
    kickY: 0,
    char: randomFrom(CHARS),
    maxOpacity: 0.18 + Math.random() * 0.38,
    opacity: 0,
    size: 13 + Math.random() * 13,
    color: randomFrom(palette()),
    charAge: 0,
    charLife: 40 + Math.floor(Math.random() * 80),
    wobble: Math.random() * Math.PI * 2,
  };
}

function respawn(p) {
  p.x = Math.random() * w;
  p.y = h + 10 + Math.random() * 100;
  p.vy = 0.25 + Math.random() * 0.65;
  p.kickX = 0;
  p.kickY = 0;
  p.char = randomFrom(CHARS);
  p.maxOpacity = 0.18 + Math.random() * 0.38;
  p.opacity = 0;
  p.size = 13 + Math.random() * 13;
  p.color = randomFrom(palette());
  p.charAge = 0;
  p.charLife = 40 + Math.floor(Math.random() * 80);
  p.wobble = Math.random() * Math.PI * 2;
}

function repelFrom(x, y, strength = 1) {
  const radius = Math.max(150, Math.min(w, h) * 0.28);
  const maxKick = 5.1 * strength;

  for (const p of particles) {
    const dx = p.x - x;
    const dy = p.y - y;
    const dist = Math.hypot(dx, dy);
    if (dist > radius) continue;

    const falloff = 1 - dist / radius;
    const force = maxKick * falloff * falloff;
    const nx = dx / (dist || 1);
    const ny = dy / (dist || 1);

    p.kickX += nx * force;
    p.kickY += ny * force;
  }
}

function draw() {
  if (paused) { animId = requestAnimationFrame(draw); return; }
  frame++;
  ctx.clearRect(0, 0, w, h);

  for (const p of particles) {
    // Move upward
    p.y -= p.vy;
    // Horizontal wobble
    p.x += Math.sin(frame * 0.018 + p.wobble) * 0.28;
    // Interaction impulse decay (touch/click ripple feel)
    p.x += p.kickX;
    p.y += p.kickY;
    p.kickX *= 0.9;
    p.kickY *= 0.9;

    // Compute opacity from vertical progress (fade in near bottom, fade out near top)
    const progress = 1 - p.y / h; // 0 = bottom, 1 = top
    if (progress < 0.12) {
      p.opacity = (progress / 0.12) * p.maxOpacity;
    } else if (progress > 0.72) {
      p.opacity = Math.max(0, ((1 - progress) / 0.28) * p.maxOpacity);
    } else {
      p.opacity = p.maxOpacity;
    }

    // Occasionally swap char
    p.charAge++;
    if (p.charAge >= p.charLife) {
      p.char = randomFrom(CHARS);
      p.charAge = 0;
    }

    // Draw
    ctx.globalAlpha = p.opacity;
    ctx.fillStyle = p.color;
    ctx.font = `${p.size}px var(--font-mono, monospace)`;
    ctx.fillText(p.char, p.x, p.y);

    // Respawn if off-screen top
    if (p.y < -24) respawn(p);
  }

  ctx.globalAlpha = 1;
  animId = requestAnimationFrame(draw);
}

function resize() {
  w = canvas.width  = window.innerWidth;
  h = canvas.height = window.innerHeight;
}

export function updateCanvasTheme(dark) {
  isDark = dark;
  // Refresh particle colors on next respawn (gradual, natural)
}

export function initCanvas() {
  canvas = document.getElementById('bg-canvas');
  if (!canvas) return;
  ctx = canvas.getContext('2d');

  isDark = document.documentElement.dataset.theme !== 'light';

  resize();
  window.addEventListener('resize', resize, { passive: true });

  const onPointerDown = (e) => {
    repelFrom(e.clientX, e.clientY, 1);
  };

  const onPointerMove = (e) => {
    if (e.pointerType === 'touch' && e.isPrimary && e.buttons === 1) {
      repelFrom(e.clientX, e.clientY, 0.65);
    }
  };

  if ('PointerEvent' in window) {
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
  } else {
    window.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      if (!t) return;
      repelFrom(t.clientX, t.clientY, 1);
    }, { passive: true });
    window.addEventListener('click', (e) => {
      repelFrom(e.clientX, e.clientY, 1);
    }, { passive: true });
  }

  // Spawn particles spread across canvas initially
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push(makeParticle(false));
  }

  // Pause when tab hidden
  document.addEventListener('visibilitychange', () => {
    paused = document.hidden;
  });

  draw();
}
